<script setup lang="ts">
/**
 * Style 10–14 Rate (3185:16687 … 3185:16791; mobile 3183:18080 … 3183:18224).
 * One outfit at a time on Quiz/Scale. Release (or Enter) saves the value and opens the
 * next outfit — no Continue, no Skip. Skeleton while the photo loads (scale inactive);
 * on a load error the answers so far stay saved.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairQuizScale } from '@/ui/patterns/FairQuiz'
import { FairButton } from '@/ui/primitives/FairButton'

const props = defineProps<{
  imageUrl: string | null
  /** Cards are being fetched. */
  loading?: boolean
  error?: boolean
}>()

const emit = defineEmits<{
  rate: [value: number]
  retry: []
}>()

const { t } = useI18n()

const value = ref<number | null>(null)
const imageLoaded = ref(false)

watch(
  () => props.imageUrl,
  () => {
    value.value = null
    imageLoaded.value = false
  },
)

const busy = computed(() => props.loading || !props.imageUrl || !imageLoaded.value)
const stepLabels = computed(() => [1, 2, 3, 4, 5].map((step) => t(`style_quiz.rate.steps.${step}`)))

function commit(next: number) {
  if (busy.value) return
  emit('rate', next)
}
</script>

<template>
  <div
    class="style-quiz-rate"
    :class="{ 'style-quiz-rate--error': error }"
  >
    <div class="style-quiz-rate__photo">
      <div
        v-if="error"
        class="style-quiz-rate__error"
        role="alert"
      >
        <p class="style-quiz-rate__error-title">
          {{ t('style_quiz.rate.error_title') }}
        </p>
        <p class="style-quiz-rate__error-text">
          {{ t('style_quiz.rate.error_text') }}
        </p>
        <FairButton
          class="style-quiz-rate__retry"
          variant="tonal"
          size="auto"
          :label="t('style_quiz.rate.retry')"
          @click="emit('retry')"
        />
      </div>
      <template v-else>
        <span
          v-if="busy"
          class="style-quiz-rate__skeleton"
          aria-hidden="true"
        />
        <img
          v-if="imageUrl"
          :key="imageUrl"
          class="style-quiz-rate__image"
          :class="{ 'style-quiz-rate__image--hidden': !imageLoaded }"
          :src="imageUrl"
          :alt="t('style_quiz.rate.photo_alt')"
          @load="imageLoaded = true"
          @error="imageLoaded = true"
        >
      </template>
    </div>

    <div class="style-quiz-rate__panel">
      <div class="fair-quiz-card__head style-quiz-rate__head">
        <h2 class="fair-quiz-card__title">
          {{ t('style_quiz.rate.title') }}
        </h2>
        <p class="fair-quiz-card__subtitle">
          {{ t('style_quiz.rate.subtitle') }}
        </p>
      </div>
      <FairQuizScale
        v-if="!error"
        v-model="value"
        class="style-quiz-rate__scale"
        :class="{ 'style-quiz-rate__scale--busy': busy }"
        :step-labels="stepLabels"
        :min-label="stepLabels[0]"
        :max-label="stepLabels[4]"
        :aria-label="t('style_quiz.rate.title')"
        :disabled="busy"
        @commit="commit"
      />
    </div>
  </div>
</template>
